import { useState, useMemo, useEffect } from "react";
import { Plus, Edit2, Trash2, Wallet, RefreshCw, Link as LinkIcon } from "lucide-react";
import { isDateInFilter } from "../layout/GlobalDateFilter";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import MetricCard from "../ui/MetricCard";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import ConfirmDialog from "../ui/ConfirmDialog";
import OneOffForm from "../tools/OneOffForm";
import ExpensesCSVImport from "./ExpensesCSVImport";
import { formatUSD } from "../../lib/formatters";
import { formatDate, monthKey, today } from "../../lib/dateUtils";
import { suggestMatch } from "../../lib/transactionMatcher";
import { useQbSync } from "../../hooks/useQbSync";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Transactions({ state, dashboard, dateFilter }) {
  const oneOffs = state.oneOffPayments || [];
  const offers = state.offers || [];
  const tools = state.tools || [];
  const team = state.team || [];
  const contractors = useMemo(() => team.filter((m) => m.type === "contractor"), [team]);
  // Partners = team members that have at least one partner_rule. Used to
  // populate the "Match to partner payout" dropdown group.
  const partners = useMemo(() => {
    const rules = state.partnerRules || [];
    const partnerIds = new Set(rules.map((r) => r.partnerId));
    return team.filter((m) => partnerIds.has(m.id));
  }, [team, state.partnerRules]);
  const departments = state.settings?.departments || [];
  // Year is derived from the global dateFilter (set in the page header).
  // "All time" falls back to the current year for the per-month matrix view
  // since stacking 5 years of monthly rows would be unreadable.
  const filterYear = dateFilter?.year && dateFilter.year !== "all" ? dateFilter.year : new Date().getFullYear();
  const year = filterYear;
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [bankStatusFilter, setBankStatusFilter] = useState("completed"); // completed / canceled / all
  const [txTypeFilter, setTxTypeFilter] = useState(""); // expense / withdrawal / refund / deposit / ""
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);

  // QuickBooks sync — Transactions tab is the unified bank-feed view
  // (QuickBooks + manual CSV import). Stripe activity lives on Payments V2.
  const qb = useQbSync({
    existingTransactions: oneOffs,
    onAddTransactions: (rows) => rows.forEach((r) => dashboard.addOneOffPayment(r)),
    onUpdateTransaction: (id, patch) => dashboard.updateOneOffPayment(id, patch),
  });
  // Default "since" = 90 days ago, ISO yyyy-mm-dd. User can change.
  const [qbSince, setQbSince] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });

  // Apply the global filter: year + optional from/to month range.
  // "All time" + no month bounds = pass-through (no filtering).
  const inYear = useMemo(
    () => oneOffs.filter((p) => isDateInFilter(p.date, dateFilter)),
    [oneOffs, dateFilter]
  );

  const categories = useMemo(() => {
    const set = new Set();
    oneOffs.forEach((p) => p.category && set.add(p.category));
    return [...set].sort();
  }, [oneOffs]);

  const isCanceledTxn = (p) => !!(p.externalStatus && /cancel/i.test(p.externalStatus));

  // Derive a high-level transaction type for the filter (refund / deposit /
  // withdrawal / expense). Withdrawal is detected by name since it's a sub-
  // category of Purchase (Money out where description contains "Withdrawal").
  const txTypeOf = (p) => {
    if (p.qbType === "Refund") return "refund";
    if (p.qbType === "Deposit") return "deposit";
    if (/withdrawal/i.test(p.name || "")) return "withdrawal";
    if ((p.direction || "out") === "in") return "deposit";
    return "expense";
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return inYear
      .filter((p) => {
        if (bankStatusFilter === "completed") return !isCanceledTxn(p);
        if (bankStatusFilter === "canceled") return isCanceledTxn(p);
        return true; // "all"
      })
      .filter((p) => !txTypeFilter || txTypeOf(p) === txTypeFilter)
      .filter((p) => !statusFilter || (p.status || "matched") === statusFilter)
      .filter((p) => !categoryFilter || p.category === categoryFilter)
      .filter((p) => {
        if (!q) return true;
        return (
          (p.name || "").toLowerCase().includes(q) ||
          (p.category || "").toLowerCase().includes(q) ||
          (p.notes || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [inYear, bankStatusFilter, txTypeFilter, statusFilter, categoryFilter, searchQuery]);

  // Real-expense rows for totals: money-OUT only. Refunds/deposits are tracked
  // separately under "incomingTotal" so the Expenses metric isn't confused
  // by money coming back in.
  const realExpenses = useMemo(
    () => inYear.filter((p) => (p.direction || "out") === "out"),
    [inYear]
  );

  // Money-in totals for context (refunds + deposits in the period).
  const incomingTotal = useMemo(
    () => inYear
      .filter((p) => (p.direction || "out") === "in")
      .reduce((s, p) => s + (Number(p.amount) || 0), 0),
    [inYear]
  );

  const summary = useMemo(() => {
    const total = realExpenses.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const cogs = realExpenses
      .filter((p) => (p.costType || "opex") === "cogs")
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const opex = realExpenses
      .filter((p) => (p.costType || "opex") === "opex")
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const flaggedCount = inYear.filter((p) => (p.status || "matched") === "flagged").length;
    return { total, cogs, opex, count: realExpenses.length, flaggedCount, incomingTotal };
  }, [inYear, realExpenses, incomingTotal]);

  // Match a transaction to a Tool or Salary source. Closes the flag.
  const matchTo = (txnId, sourceType, sourceId) => {
    dashboard.updateOneOffPayment(txnId, {
      status: "matched",
      matchedSourceType: sourceType,
      matchedSourceId: sourceId,
    });
  };

  // Clear all match tags, send back to Needs review.
  const reopenFlag = (txnId) => {
    dashboard.updateOneOffPayment(txnId, {
      status: "flagged",
      matchedSourceType: null,
      matchedSourceId: null,
    });
  };

  // Inline "create a new tool / contractor" flow triggered from the dropdowns.
  // When the user picks "new:recurring", "new:one_off", or "new:contractor",
  // we capture the kind and open a small prompt for the name. On submit we
  // create the record, then match the captured rows to it.
  const [newPrompt, setNewPrompt] = useState(null); // { kind: "recurring"|"one_off"|"contractor", targetIds }

  // Apply a bulk status change to every selected row.
  // Match types that don't reference an entity ID — they just flag the row
  // as "not a regular business expense" for the P&L calc (owner draw, bank
  // transfer between accounts, or revenue already counted in Payments V2).
  const NON_ENTITY_MATCH_TYPES = new Set(["owner_withdrawal", "bank_transfer", "revenue_dedup"]);

  // Action string encoding:
  //   "tool:<id>" / "salary:<id>" / "contractor:<id>"
  //   / "owner_withdrawal" / "bank_transfer" / "revenue_dedup" / "reopen"
  //   / "new:recurring" / "new:one_off" / "new:contractor"
  const bulkApply = (action) => {
    if (!action || selectedIds.size === 0) return;
    const ids = [...selectedIds];
    if (action === "reopen") {
      ids.forEach(reopenFlag);
    } else if (action.startsWith("tool:")) {
      const toolId = action.slice(5);
      ids.forEach((id) => matchTo(id, "tool", toolId));
    } else if (action.startsWith("salary:")) {
      const memberId = action.slice(7);
      ids.forEach((id) => matchTo(id, "salary", memberId));
    } else if (action.startsWith("contractor:")) {
      const contractorId = action.slice(11);
      ids.forEach((id) => matchTo(id, "contractor", contractorId));
    } else if (action.startsWith("partner_payout:")) {
      const partnerId = action.slice("partner_payout:".length);
      ids.forEach((id) => matchTo(id, "partner_payout", partnerId));
    } else if (NON_ENTITY_MATCH_TYPES.has(action)) {
      // No entity ID — just tag the source type so P&L knows to skip it.
      ids.forEach((id) => matchTo(id, action, null));
    } else if (action === "new:recurring" || action === "new:one_off" || action === "new:contractor") {
      // Defer the actual match until the user has named the new record.
      setNewPrompt({ kind: action.slice(4), targetIds: ids });
      return; // don't clear selection
    }
    clearSelection();
  };

  // Called by the NewPrompt modal after the user has entered a name.
  // Creates the tool/contractor, matches the captured rows, clears selection.
  const createAndMatch = (name) => {
    if (!newPrompt || !name.trim()) { setNewPrompt(null); return; }
    const ids = newPrompt.targetIds || [];
    if (newPrompt.kind === "contractor") {
      const created = dashboard.addTeamMember({
        name: name.trim(),
        type: "contractor",
        status: "active",
      });
      if (created?.id) {
        ids.forEach((id) => matchTo(id, "contractor", created.id));
      }
    } else {
      const created = dashboard.addTool({
        name: name.trim(),
        type: newPrompt.kind, // "recurring" or "one_off"
        status: "active",
        unitLabel: newPrompt.kind === "one_off" ? "purchase" : "seats",
      });
      if (created?.id) {
        ids.forEach((id) => matchTo(id, "tool", created.id));
      }
    }
    setNewPrompt(null);
    clearSelection();
  };

  const monthlyTotals = MONTH_LABELS.map((_, i) => {
    const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
    return filtered
      .filter((p) => monthKey(p.date) === mKey)
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  });
  const filteredYearTotal = monthlyTotals.reduce((a, b) => a + b, 0);
  const currentMonthKey = `${today().getFullYear()}-${String(today().getMonth() + 1).padStart(2, "0")}`;

  const toggleOne = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));
  const someFilteredSelected = filtered.some((p) => selectedIds.has(p.id));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display font-semibold text-lg text-brand-ink dark:text-[var(--text-primary)]">Expenses</h2>
        <div className="flex flex-wrap items-center gap-2">
          {qb.connected === false && (
            <Button variant="secondary" onClick={qb.connect} title="Connect QuickBooks to auto-sync card transactions">
              <LinkIcon size={14} /> Connect QuickBooks
            </Button>
          )}
          {qb.connected === true && (
            <div className="flex items-center gap-1.5">
              <label className="text-[11px] text-brand-slate" title="Pull QuickBooks transactions from this date forward">QB from:</label>
              <input
                type="date"
                value={qbSince}
                onChange={(e) => setQbSince(e.target.value)}
                disabled={qb.syncing}
                className="px-2 py-1 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
              />
              <Button
                variant="secondary"
                onClick={() => qb.sync({ since: qbSince })}
                disabled={qb.syncing || !qbSince}
                title={qb.lastSyncedAt ? `Last synced ${new Date(qb.lastSyncedAt).toLocaleString()}` : "Sync from QuickBooks"}
              >
                <RefreshCw size={14} className={qb.syncing ? "animate-spin" : ""} />
                {qb.syncing ? "Syncing..." : "Sync QuickBooks"}
              </Button>
            </div>
          )}
          <ExpensesCSVImport
            offers={offers}
            departments={departments}
            onCommit={(rows) => rows.forEach((r) => dashboard.addOneOffPayment(r))}
          />
          <Button onClick={() => setShowAdd(true)}><Plus size={14} /> Add transaction</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label={`${year} total`} value={formatUSD(summary.total)} accent="amber" sublabel={`${summary.count} expenses`} />
        <MetricCard label="COGS spend" value={formatUSD(summary.cogs)} accent="green" sublabel="direct delivery" />
        <MetricCard label="OPEX spend" value={formatUSD(summary.opex)} accent="orange" sublabel="overhead" />
        <div
          onClick={() => setStatusFilter(summary.flaggedCount > 0 ? "flagged" : "")}
          className={summary.flaggedCount > 0 ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}
          role={summary.flaggedCount > 0 ? "button" : undefined}
        >
          <MetricCard
            label="Needs review"
            value={String(summary.flaggedCount)}
            accent={summary.flaggedCount > 0 ? "red" : "green"}
            sublabel={summary.flaggedCount > 0 ? "click to filter" : "all matched"}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name, category, notes..."
            className="pl-7 pr-7 py-1.5 border border-gray-200 rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30 min-w-[220px]"
          />
          <svg className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-slate pointer-events-none" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-slate hover:text-brand-ink text-sm leading-none"
              title="Clear"
            >
              ×
            </button>
          )}
        </div>
        <select
          value={txTypeFilter}
          onChange={(e) => setTxTypeFilter(e.target.value)}
          className={`px-3 py-1.5 border rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)] ${
            txTypeFilter ? "border-brand-blue text-brand-blue-dark font-medium" : "border-gray-200"
          }`}
        >
          <option value="">All transaction types</option>
          <option value="expense">Expenses only</option>
          <option value="withdrawal">Withdrawals only</option>
          <option value="refund">Refunds only</option>
          <option value="deposit">Deposits / Money in only</option>
        </select>
        <select
          value={bankStatusFilter}
          onChange={(e) => setBankStatusFilter(e.target.value)}
          className={`px-3 py-1.5 border rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)] ${
            bankStatusFilter === "canceled"
              ? "border-gray-400 text-gray-600 font-medium"
              : "border-gray-200"
          }`}
        >
          <option value="completed">Completed only</option>
          <option value="canceled">Canceled only</option>
          <option value="all">All bank statuses</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={`px-3 py-1.5 border rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)] ${
            statusFilter === "flagged"
              ? "border-red-300 text-red-700 font-medium"
              : "border-gray-200"
          }`}
        >
          <option value="">All review statuses</option>
          <option value="flagged">Needs review</option>
          <option value="matched">Matched / closed</option>
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:border-[var(--border-soft)]"
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {(bankStatusFilter !== "completed" || txTypeFilter || statusFilter || categoryFilter || searchQuery) && (
          <span className="text-[11px] text-brand-slate">
            Showing {filtered.length} of {inYear.length}
          </span>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-brand-blue-soft border border-blue-200">
          <span className="text-sm font-medium text-brand-blue-dark">{selectedIds.size} selected</span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-brand-slate">Set status:</span>
            <select
              value=""
              onChange={(e) => { if (e.target.value) bulkApply(e.target.value); }}
              className="px-2 py-1.5 border border-gray-200 dark:border-[var(--border-soft)] rounded-lg text-xs bg-white dark:bg-[var(--bg-surface)] dark:text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-brand-blue/30"
              title="Apply a status to every selected row"
            >
              <option value="">Choose action...</option>
              <optgroup label="Match to recurring tool">
                {[...tools]
                  .filter((t) => (t.type || "recurring") === "recurring")
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((t) => (
                    <option key={t.id} value={`tool:${t.id}`}>{t.name}</option>
                  ))}
                <option value="new:recurring">+ New recurring tool…</option>
              </optgroup>
              <optgroup label="Match to one-off tool">
                {[...tools]
                  .filter((t) => t.type === "one_off")
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((t) => (
                    <option key={t.id} value={`tool:${t.id}`}>{t.name}</option>
                  ))}
                <option value="new:one_off">+ New one-off tool…</option>
              </optgroup>
              <optgroup label="Match to contractor">
                {[...contractors]
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((c) => (
                    <option key={c.id} value={`contractor:${c.id}`}>{c.name}</option>
                  ))}
                <option value="new:contractor">+ New contractor…</option>
              </optgroup>
              {partners.length > 0 && (
                <optgroup label="Match to partner payout">
                  {[...partners]
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((p) => (
                      <option key={p.id} value={`partner_payout:${p.id}`}>{p.name}</option>
                    ))}
                </optgroup>
              )}
              <optgroup label="Not a business expense">
                <option value="owner_withdrawal">Owner withdrawal (Ahmed draw)</option>
                <option value="bank_transfer">Bank transfer (between accounts)</option>
                <option value="revenue_dedup">Revenue (already in Payments V2)</option>
              </optgroup>
              <optgroup label="Other">
                <option value="reopen">Send to Needs review</option>
              </optgroup>
            </select>
            <Button variant="secondary" size="sm" onClick={clearSelection}>Clear</Button>
            <Button variant="danger" size="sm" onClick={() => setConfirmBulk(true)}>
              <Trash2 size={13} /> Delete {selectedIds.size}
            </Button>
          </div>
        </div>
      )}

      <Card>
        {filtered.length === 0 ? (
          <EmptyState icon={Wallet} title="No transactions yet" message={`Add one or upload a CSV to track ${year} business spending.`} />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH className="w-8">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    ref={(el) => { if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected; }}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedIds(new Set(filtered.map((p) => p.id)));
                      else clearSelection();
                    }}
                    className="cursor-pointer"
                  />
                </TH>
                <TH>Status</TH>
                <TH>Date</TH>
                <TH>Description</TH>
                <TH>Category</TH>
                <TH className="text-right">Amount</TH>
                <TH>Notes</TH>
                <TH></TH>
              </TR>
            </THead>
            <tbody>
              {filtered.map((p) => {
                const status = p.status || "matched";
                const isFlagged = status === "flagged";
                const matchedTool = p.matchedSourceType === "tool" ? tools.find((t) => t.id === p.matchedSourceId) : null;
                const matchedMember = p.matchedSourceType === "salary" ? team.find((m) => m.id === p.matchedSourceId) : null;
                const matchedContractor = p.matchedSourceType === "contractor" ? team.find((m) => m.id === p.matchedSourceId) : null;
                const matchedPartner = p.matchedSourceType === "partner_payout" ? team.find((m) => m.id === p.matchedSourceId) : null;
                const matchedNonExpense = NON_ENTITY_MATCH_TYPES.has(p.matchedSourceType) ? p.matchedSourceType : null;

                let statusValue = "matched";
                if (isFlagged) statusValue = "flagged";
                else if (matchedTool) statusValue = `tool:${matchedTool.id}`;
                else if (matchedMember) statusValue = `salary:${matchedMember.id}`;
                else if (matchedContractor) statusValue = `contractor:${matchedContractor.id}`;
                else if (matchedPartner) statusValue = `partner_payout:${matchedPartner.id}`;
                else if (matchedNonExpense) statusValue = matchedNonExpense;

                const onStatusChange = (val) => {
                  if (val === "flagged") return reopenFlag(p.id);
                  if (val === "matched") return;
                  // "new:<kind>:<rowId>" → open inline create prompt and
                  // capture this single row as the match target.
                  if (val.startsWith("new:")) {
                    const parts = val.split(":");
                    const kind = parts[1];
                    const rowId = parts[2];
                    setNewPrompt({ kind, targetIds: [rowId] });
                    return;
                  }
                  // No-entity match types ("owner_withdrawal", etc.) carry
                  // no ID — just tag the source type so P&L skips the row.
                  if (NON_ENTITY_MATCH_TYPES.has(val)) {
                    return matchTo(p.id, val, null);
                  }
                  const [type, id] = val.split(":");
                  matchTo(p.id, type, id);
                };

                const statusPillClass = isFlagged
                  ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300"
                  : "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300";

                // Auto-suggestion shown only when flagged + no current source
                const suggestion = isFlagged ? suggestMatch(p, { tools, team }) : null;

                return (
                  <TR key={p.id}>
                    <TD className="w-8">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(p.id)}
                        onChange={() => toggleOne(p.id)}
                        className="cursor-pointer"
                      />
                    </TD>
                    <TD>
                      <select
                        value={statusValue}
                        onChange={(e) => onStatusChange(e.target.value)}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border focus:outline-none focus:ring-1 focus:ring-brand-blue/30 max-w-[160px] ${statusPillClass}`}
                        title={suggestion ? `Suggestion: match to ${suggestion.sourceName}` : ""}
                      >
                        <option value="flagged">⚠ Needs review</option>
                        <option value="matched" disabled>✓ Matched (no source)</option>
                        <optgroup label="Match to recurring tool">
                          {tools
                            .filter((t) => (t.type || "recurring") === "recurring")
                            .map((t) => <option key={t.id} value={`tool:${t.id}`}>{t.name}</option>)}
                          <option value={`new:recurring:${p.id}`}>+ New recurring tool…</option>
                        </optgroup>
                        <optgroup label="Match to one-off tool">
                          {tools
                            .filter((t) => t.type === "one_off")
                            .map((t) => <option key={t.id} value={`tool:${t.id}`}>{t.name}</option>)}
                          <option value={`new:one_off:${p.id}`}>+ New one-off tool…</option>
                        </optgroup>
                        <optgroup label="Match to contractor">
                          {contractors
                            .slice()
                            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                            .map((c) => <option key={c.id} value={`contractor:${c.id}`}>{c.name}</option>)}
                          <option value={`new:contractor:${p.id}`}>+ New contractor…</option>
                        </optgroup>
                        {partners.length > 0 && (
                          <optgroup label="Match to partner payout">
                            {partners
                              .slice()
                              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                              .map((pp) => <option key={pp.id} value={`partner_payout:${pp.id}`}>{pp.name}</option>)}
                          </optgroup>
                        )}
                        <optgroup label="Not a business expense">
                          <option value="owner_withdrawal">Owner withdrawal (Ahmed draw)</option>
                          <option value="bank_transfer">Bank transfer (between accounts)</option>
                          <option value="revenue_dedup">Revenue (already in Payments V2)</option>
                        </optgroup>
                      </select>
                    </TD>
                    <TD>{formatDate(p.date)}</TD>
                    <TD className="font-medium">
                      {p.name}
                      {p.qbType && (
                        <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide border ${
                          p.qbType === "Deposit"     ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          p.qbType === "Refund"      ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          p.qbType === "Transfer"    ? "bg-purple-50 text-purple-700 border-purple-200" :
                          p.qbType === "BillPayment" ? "bg-blue-50 text-blue-700 border-blue-200" :
                                                       "bg-gray-100 text-gray-600 border-gray-200"
                        }`}>
                          {p.qbType}
                        </span>
                      )}
                      {p.externalStatus && /cancel/i.test(p.externalStatus) && (
                        <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide border bg-gray-100 text-gray-500 border-gray-200">
                          Canceled
                        </span>
                      )}
                    </TD>
                    <TD className="text-brand-slate text-xs">{p.category || "—"}</TD>
                    <TD className={`text-right font-medium ${
                      (p.direction || "out") === "in" || Number(p.amount) < 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-red-600 dark:text-red-400"
                    }`}>
                      {(p.direction || "out") === "in"
                        ? `+${formatUSD(Math.abs(Number(p.amount)))}`
                        : Number(p.amount) < 0
                          ? `+${formatUSD(Math.abs(Number(p.amount)))}`
                          : formatUSD(p.amount)}
                      {p.currency && p.currency !== "USD" && (
                        <span className="ml-1 text-[9px] font-semibold text-brand-slate uppercase">{p.currency}</span>
                      )}
                    </TD>
                    <TD className="text-brand-slate text-xs max-w-[200px] truncate">{p.notes}</TD>
                    <TD>
                      <div className="flex justify-end gap-1">
                        <button className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-soft)] text-brand-slate" onClick={() => setEditing(p)}>
                          <Edit2 size={13} />
                        </button>
                        {/* Auto-synced (QuickBooks) rows can't be deleted — they would
                            just re-appear on the next sync. Only manually-added rows
                            and CSV-imported rows can be removed here. */}
                        {!(p.externalId && p.externalId.startsWith("qb_")) && (
                          <button className="p-1.5 rounded hover:bg-red-50 text-red-600" onClick={() => setConfirm({
                            title: "Delete transaction",
                            message: `Delete ${p.name} (${formatUSD(p.amount)})?`,
                            confirmLabel: "Delete",
                            onConfirm: () => dashboard.deleteOneOffPayment(p.id),
                          })}>
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
            <tfoot>
              {(() => {
                let total = 0;
                for (const p of filtered) {
                  const sign = (p.direction || "out") === "out" ? 1 : -1;
                  total += sign * (Number(p.amount) || 0);
                }
                return (
                  <tr className="border-t-2 border-gray-200 dark:border-[var(--border-soft)] bg-gray-50 dark:bg-[var(--bg-soft)] font-semibold text-xs">
                    <td colSpan={5} className="px-3 py-2 text-brand-ink dark:text-[var(--text-primary)]">
                      Subtotal of {filtered.length} {filtered.length === 1 ? "transaction" : "transactions"} shown
                    </td>
                    <td className={`px-3 py-2 text-right ${total < 0 ? "text-emerald-700 dark:text-emerald-300" : ""}`}>
                      {formatUSD(total)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                );
              })()}
            </tfoot>
          </Table>
        )}
      </Card>

      {filtered.length > 0 && (
        <Card title="Monthly totals" action={<span className="text-sm font-medium text-brand-ink dark:text-[var(--text-primary)]">{formatUSD(filteredYearTotal)} total</span>}>
          <div className="matrix-scroll overflow-x-auto rounded-lg border border-gray-100 dark:border-[var(--border-soft)]">
            <table className="w-max text-xs">
              <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
                <tr>
                  {MONTH_LABELS.map((m, i) => {
                    const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
                    const isCurrent = mKey === currentMonthKey;
                    return (
                      <th key={m} className={`px-3 py-2 text-center font-medium uppercase tracking-wide whitespace-nowrap min-w-[80px] ${isCurrent ? "bg-brand-blue-soft text-brand-blue-dark" : "text-brand-slate"}`}>
                        {m}
                      </th>
                    );
                  })}
                  <th className="px-3 py-2 text-right font-medium text-brand-slate uppercase tracking-wide whitespace-nowrap min-w-[100px] bg-gray-100 dark:bg-[var(--bg-page)]">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-brand-pale-gray dark:bg-[var(--bg-soft)] font-semibold">
                  {monthlyTotals.map((v, i) => {
                    const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
                    const isCurrent = mKey === currentMonthKey;
                    return (
                      <td key={i} className={`px-3 py-2 text-center ${isCurrent ? "bg-brand-blue-soft" : ""}`}>
                        {v > 0 ? formatUSD(v) : "—"}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right">{formatUSD(filteredYearTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <OneOffForm
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSubmit={dashboard.addOneOffPayment}
        offers={offers}
        departments={departments}
      />
      <OneOffForm
        open={!!editing}
        onClose={() => setEditing(null)}
        onSubmit={(data) => editing && dashboard.updateOneOffPayment(editing.id, data)}
        initial={editing || undefined}
        offers={offers}
        departments={departments}
      />
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={confirm?.onConfirm || (() => {})}
      />
      <ConfirmDialog
        open={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        title={`Delete ${selectedIds.size} expenses?`}
        message={`This will permanently delete ${selectedIds.size} expense${selectedIds.size > 1 ? "s" : ""}. Cannot be undone.`}
        confirmLabel={`Delete ${selectedIds.size}`}
        onConfirm={() => {
          [...selectedIds].forEach((id) => dashboard.deleteOneOffPayment(id));
          clearSelection();
        }}
      />

      <NewPrompt
        prompt={newPrompt}
        onCancel={() => setNewPrompt(null)}
        onSubmit={(name) => createAndMatch(name)}
      />
    </div>
  );
}

// Inline prompt: opens when user picks "+ New recurring tool" /
// "+ New one-off tool" / "+ New contractor" from a Status dropdown. Just
// asks for a name, then creates the record and matches captured row(s).
function NewPrompt({ prompt, onCancel, onSubmit }) {
  const [name, setName] = useState("");
  useEffect(() => {
    if (prompt) setName("");
  }, [prompt]);
  if (!prompt) return null;
  const { kind } = prompt;
  const count = (prompt.targetIds || []).length;
  const config = {
    one_off: { title: "New one-off tool", label: "Tool name *", placeholder: "e.g. Million Verifier credits", noun: "tool" },
    recurring: { title: "New recurring tool", label: "Tool name *", placeholder: "e.g. Slack", noun: "tool" },
    contractor: { title: "New contractor", label: "Contractor name *", placeholder: "e.g. FiverrEU", noun: "contractor" },
  }[kind] || { title: "New", label: "Name *", placeholder: "", noun: "record" };
  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    onSubmit(name);
  };
  return (
    <Modal open={!!prompt} onClose={onCancel} title={config.title} size="sm">
      <form onSubmit={handleSubmit} className="space-y-3">
        <p className="text-xs text-brand-slate">
          {count} expense{count === 1 ? "" : "s"} will be matched to this {config.noun} once created.
        </p>
        <Input
          label={config.label}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={config.placeholder}
          autoFocus
        />
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={!name.trim()}>
            Create &amp; match {count > 0 ? `(${count})` : ""}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
